import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteAccount } from '../api/userProfile';
import { useAuth } from '../context/AuthContext';

const CONFIRM_PHRASE = 'DELETE';

const DeleteAccountSection: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (confirmText !== CONFIRM_PHRASE) return;
    setLoading(true);
    setError('');
    try {
      await deleteAccount();
      logout();
      navigate('/');
    } catch {
      setError('Something went wrong. Please try again or contact support@buildon.co.za.');
      setLoading(false);
    }
  };

  return (
    <>
      <section className="mt-10 pt-8 border-t border-outline-variant">
        <h2 className="text-lg font-semibold text-error mb-1">Delete account</h2>
        <p className="text-sm text-on-surface-variant mb-4">
          Permanently deletes your account and all associated personal data - profile, messages, reviews,
          jobs, and credentials. This cannot be undone. Under POPIA you have the right to request this at
          any time.
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-error text-error text-sm font-semibold hover:bg-error/5 active:scale-95 transition-all"
        >
          <span className="material-symbols-outlined text-[18px]">delete_forever</span>
          Delete my account
        </button>
      </section>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-surface rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-error text-3xl mt-0.5">warning</span>
              <div>
                <h3 className="font-bold text-on-surface text-lg">Delete your account?</h3>
                <p className="text-sm text-on-surface-variant mt-1">
                  This will permanently delete:
                </p>
                <ul className="text-sm text-on-surface-variant list-disc pl-4 mt-2 space-y-0.5">
                  <li>Your profile and personal information</li>
                  <li>All messages you have sent or received</li>
                  <li>All reviews you have written</li>
                  <li>All jobs you have posted</li>
                  <li>Your contractor profile and credentials (if applicable)</li>
                </ul>
                <p className="text-sm text-on-surface-variant mt-3">
                  This action <strong>cannot be undone</strong>.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                Type <span className="font-bold text-error">{CONFIRM_PHRASE}</span> to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="w-full border border-outline-variant rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-error"
                autoComplete="off"
              />
            </div>

            {error && (
              <p className="text-sm text-error">{error}</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setShowModal(false); setConfirmText(''); setError(''); }}
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg border border-outline-variant text-on-surface text-sm font-semibold hover:bg-surface-container transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={confirmText !== CONFIRM_PHRASE || loading}
                className="flex-1 px-4 py-2 rounded-lg bg-error text-on-error text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-error/90 active:scale-95 transition-all"
              >
                {loading ? 'Deleting...' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteAccountSection;
